/**
 * T051 (catalogue-sku-editor). Reads `GET /units` and `GET /skus/{id}/units`, and composes
 * `SkuUnitEditor` (T050/T052 — built in this same pass, see components/sku-unit-editor.tsx's own
 * header) for the `PUT /skus/{id}/units` mutation. Page shell (PageHeader) copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/create-category.tsx (the
 * same source ../brands/Detail.tsx cites for its own page layout) — the editor itself is the only
 * form on the page. 2026-09-29: full width like every other edit screen, the SKU's sibling screens
 * in the `RecordSummary` strip rather than a back button in the header.
 *
 * §8.2: anyone holding `catalogue.read` sees the unit rows, read-only, through the template's
 * `LegacyTable` (packages/isomorphic-core/src/components/legacy-table, as reports/ReapplyPreview.tsx
 * uses it); only `catalogue.administer` gets the editor.
 */
import { useState } from "react";
import { Link, useLoaderData, useOutletContext, type LoaderFunctionArgs } from "react-router-dom";
import toast from "react-hot-toast";
import { useGoBack } from "../../../lib/navigation";
import { skuPath } from "../../../lib/paths";
import { PiCurrencyInrDuotone as PriceIcon, PiPencilSimpleDuotone as EditIcon } from "react-icons/pi";
import { PageHeader, ErrorSurface } from "@steelyard/theme";
import { byKey, getSku, getSkuBySlug, getSkuUnits, listUnits, setSkuUnits, type SellableUnitInput } from "../../../lib/services/catalog";
import { SkuUnitEditor } from "../../../components/sku-unit-editor";
import { RecordSummary } from "../../../components/detail-parts";
import { ApiError } from "../../../lib/api";
import { can } from "../../../lib/permissions";
import type { AccountSummary, PermissionState } from "../../../types";
import { Button } from "rizzui";
import LegacyTable from "@core/components/legacy-table";

const yesNo = (value: boolean) => (value ? "Yes" : "No");

const readOnlyColumns = [
  { title: "Unit", dataIndex: "unitCode", key: "unitCode" },
  { title: "Conversion to stocking unit", dataIndex: "conversionToStocking", key: "conversionToStocking" },
  { title: "Primary", dataIndex: "isPrimary", key: "isPrimary", render: yesNo },
  { title: "Offered for sale", dataIndex: "isSellable", key: "isSellable", render: yesNo },
];

export async function skuUnitsLoader({ params, request }: LoaderFunctionArgs) {
  const [sku, units] = await Promise.all([byKey(request, params.slug!, getSku, getSkuBySlug, skuPath), listUnits()]);
  const skuUnits = await getSkuUnits(sku.id);
  if (!skuUnits || !units || !sku) throw new Error("The API returned no body for this SKU's units.");
  return { skuUnits, units, sku };
}

export default function SkuUnits() {
  const { skuUnits, units, sku } = useLoaderData() as Awaited<ReturnType<typeof skuUnitsLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const skuHref = skuPath(sku.slug);
  const goBack = useGoBack(skuHref);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | undefined>();
  const rows = skuUnits.units;

  if (!can(permissionState, "catalogue.read")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="view this SKU's units" />;
  }
  const canWrite = can(permissionState, "catalogue.administer");

  async function handleSave(units: SellableUnitInput[]) {
    setSaveError(undefined);
    setBusy(true);
    try {
      const result = await setSkuUnits(sku.id, units);
      toast.success(`${result.sellableUnitCount} sellable unit(s) saved`);
      goBack();
    } catch (err) {
      // Constitution VI: the API's 422 rule name, verbatim — never paraphrased (T052).
      setSaveError(err instanceof ApiError ? (err.detail ?? err.title ?? "The service refused this set.") : "The service refused this set.");
      toast.error("Could not save the sellable units");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader
        title={`${sku.name} — Sellable Units`}
        breadcrumb={[
          { name: "Home", href: "/" },
          { name: "Catalogue · SKUs", href: "/catalogue/skus" },
          { name: sku.code, href: skuHref },
          { name: "Units" },
        ]}
      />

      <RecordSummary
        code={sku.code}
        links={
          <>
            <Link to={skuHref}>
              <Button as="span" variant="outline" size="sm">
                <EditIcon aria-hidden className="me-1.5 size-4" />
                Edit SKU
              </Button>
            </Link>
            {can(permissionState, "pricing.read") ? (
              <Link to={`${skuHref}/base-prices`}>
                <Button as="span" variant="outline" size="sm">
                  <PriceIcon aria-hidden className="me-1.5 size-4" />
                  Base prices
                </Button>
              </Link>
            ) : null}
          </>
        }
      />

      {canWrite ? (
        <SkuUnitEditor
          initialUnits={rows}
          units={units.items}
          stockingUnitId={skuUnits.stockingUnitId}
          onSave={handleSave}
          onCancel={goBack}
          busy={busy}
          error={saveError}
        />
      ) : (
        <LegacyTable
          data={rows}
          columns={readOnlyColumns}
          variant="modern"
          caption={<span className="sr-only">{`${sku.code} units`}</span>}
          rowKey="unitId"
        />
      )}
    </div>
  );
}
